import React, { useCallback, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { addFeatureFlagAccessed as featureFlagAccessedUFOv2 } from '@atlaskit/react-ufo/feature-flags-accessed';
import { captureException } from '@sentry/react';
import { History } from 'history';

import { useAnalytics } from '@townsquare/analytics';
import { AppState, useAppStateStore } from '@townsquare/app-state-store';
import { CollabContext, type CollaborationContext } from '@townsquare/collab-context';
import { useAsyncEffect } from '@townsquare/hooks';
import {
	setSentryAAID,
	setSentryCloudId,
	addFeatureFlagAccessed as featureFlagAccessedSentry,
} from '@townsquare/logging/sentry';
import { initBM3 } from '@townsquare/monitoring/bm3';
import { initReactUFO } from '@townsquare/monitoring/react-ufo';
import {
	initUFO,
	addFeatureFlagAccessed as featureFlagAccessedUFOv1,
} from '@townsquare/monitoring/ufo';
import { useOrgContextActions } from '@townsquare/org-context';
import { createOrgScopedPath } from '@townsquare/router/create-route';
import { preloadRouteComponent } from '@townsquare/router/preload';
import type { GlobalHomePageRoute, SiteHomePageRoute } from '@townsquare/router/route-types';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { NoopRbacProvider } from '@townsquare/rovo/entitlement';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import {
	initialiseConfigFromBootstrapOnce,
	initialiseFeatureGatesFromBootstrapOnce,
} from '@townsquare/stat-sig/bootstrap';
import { initConfigClient } from '@townsquare/stat-sig/config';
import { featureGatesOptions, observabilityFilter } from '@townsquare/stat-sig/options';
import { setupPlatformGates } from '@townsquare/stat-sig/setup-platform';
import { useCurrentUser } from '@townsquare/user-store';
import { User } from '@townsquare/user-store/types';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';

/**
 * Normalised data the bootstrap populator flattens into before mounting
 * the shell. The branch-specific work (Relay unmask, capability checks)
 * happens upstream — the shell only sees plain data. Designed so a future
 * second populator (e.g. server-inlined payload) can target the same shape.
 */
export type BootstrapShellData = {
	orgId: string | undefined;
	cloudIds: string[];
	/**
	 * Populator-precomputed `null`-aware view of `cloudIds`, sent through to
	 * `collabContext.orgCloudIds`. Distinguished from `cloudIds: []` so
	 * downstream consumers can tell "we know the list is empty" from "we
	 * never received a list".
	 */
	orgCloudIds: string[] | null;
	workspaceIds: string[];
	workspaceUuids: string[];
	contextCloudId: string | null;
	collabContextSource: {
		cloudIds: string[];
		isMultiSite: boolean | undefined;
		hasMultipleUnits: boolean | undefined;
		collabContextSiteAris: string[];
	} | null;
	/** Workspace selected by the active context, if any. Used only for its `cloudId` + `uuid`. */
	activeWorkspace: { cloudId: string; uuid: string } | undefined;
	/** Precomputed by the populator (branch-specific capability check). */
	userHasProjectAccess: boolean;
	userHasGoalAccess: boolean;
};

export type BootstrapShellChildContext = {
	collabContext: CollaborationContext;
	currentUser: User;
};

type BootstrapShellProps = {
	bootstrap: BootstrapShellData;
	/** Pre-built by the caller via its branch-specific `initWorkspaceFromHome*` action. */
	workspaceDetails: WorkspaceDetails | undefined;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	children: (ctx: BootstrapShellChildContext) => React.ReactNode;
};

function isGlobalRoute(route: MatchedRoute['route']): route is GlobalHomePageRoute {
	return (route as GlobalHomePageRoute | SiteHomePageRoute)?.routeType === 'global';
}

/**
 * Branch-agnostic home bootstrap side-effects. Lives downstream of the
 * `Bootstrap` populator, which flattens its Relay wire data into
 * `BootstrapShellData` before mounting the shell.
 *
 * WHAT BELONGS HERE: anything whose behaviour doesn't depend on data source —
 * org context, collab-context provider, monitoring, analytics, Statsig init,
 * route preload, project/goal-app-access registration, `AppState.FULLY_INITIALISED`.
 * WHAT BELONGS IN THE POPULATOR: anything that needs the raw wire data shape
 * (Relay unmask, fragment-ref helpers).
 *
 * TODO: render-phase side effects (here and in the populator) should move to
 * `useEffect` to play nicely with StrictMode. Out of scope.
 */
export function BootstrapShell(props: BootstrapShellProps): JSX.Element {
	const { bootstrap, workspaceDetails, matchedRoute, history } = props;
	const {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource,
		activeWorkspace,
		userHasProjectAccess,
		userHasGoalAccess,
	} = bootstrap;

	if (activeWorkspace) {
		userAccessRedirectContextHolder.setProjectAppAccess(
			activeWorkspace.cloudId,
			userHasProjectAccess,
		);
		userAccessRedirectContextHolder.setGoalAppAccess(activeWorkspace.cloudId, userHasGoalAccess);
	}

	const [{ appState }, { setAppState }] = useAppStateStore();
	const currentUser = useCurrentUser();
	const analytics = useAnalytics();
	const orgContextActions = useOrgContextActions();

	if (orgId) {
		orgContextActions.setOrgContext({
			orgId,
			cloudIds,
			workspaceIds,
			workspaceUuids,
		});

		// This should only trigger for global routes
		if (
			matchedRoute?.match.params.orgIdentifier !== orgId &&
			matchedRoute?.route &&
			isGlobalRoute(matchedRoute.route) &&
			matchedRoute.route.name !== 'not-found'
		) {
			const { path, pathPattern } = matchedRoute.route;
			const queryParams = Object.fromEntries(createSearchParamsFromCurrentUrl());
			history.replace(
				createOrgScopedPath(`${pathPattern}${path}`, orgId, queryParams, matchedRoute.match.params),
			);
		}
	}

	const cloudContext = activeWorkspace?.cloudId ?? contextCloudId;

	const collabContext = useMemo<CollaborationContext>(
		() => ({
			// Match master: goal/projectWorkspace gated on activeWorkspace existing,
			// rovoWorkspace falls back to contextCloudId.
			goalWorkspace: activeWorkspace?.cloudId ?? null,
			projectWorkspace: activeWorkspace?.cloudId ?? null,
			rovoWorkspace: cloudContext,
			contextId: activeWorkspace?.cloudId
				? `ari:cloud:townsquare::site/${activeWorkspace.cloudId}`
				: null,
			// CollaborationContext expects mutable arrays; `BootstrapShellData`
			// uses `ReadonlyArray` to guard against mutation. Copy at the boundary.
			orgCloudIds: orgCloudIds ? [...orgCloudIds] : null,
			numWorkspaces: workspaceIds.length,
			collabContextScoping: {
				collabContextCloudIds: collabContextSource?.cloudIds,
				isMultiSite: collabContextSource?.isMultiSite,
				hasMultipleUnits: collabContextSource?.hasMultipleUnits,
				collabContextSiteAris: collabContextSource?.collabContextSiteAris
					? [...collabContextSource.collabContextSiteAris]
					: undefined,
			},
		}),
		[
			cloudContext,
			orgCloudIds,
			activeWorkspace?.cloudId,
			collabContextSource?.cloudIds,
			collabContextSource?.isMultiSite,
			collabContextSource?.hasMultipleUnits,
			collabContextSource?.collabContextSiteAris,
			workspaceIds.length,
		],
	);

	if (cloudContext) {
		setSentryCloudId(cloudContext);
	}
	setSentryAAID(currentUser.accountId);

	const completeInit = useCallback(() => {
		// TODO - [TC-10918] remove legacy use of browserMetrics in favour of react-ufo
		initBM3(analytics.getAnalyticsWebClient(), 'townsquare');
		initUFO(analytics.getAnalyticsWebClient(), 'townsquare');
		initReactUFO(analytics.getAnalyticsWebClient(), routes);

		analytics.setCurrentUserAndTenantInfo(
			currentUser.accountId,
			workspaceDetails?.UUID,
			cloudContext ?? undefined,
		);
		analytics.setTopLevelAttributes({ townsquareApp: 'home' });

		setAppState(AppState.FULLY_INITIALISED);
	}, [analytics, currentUser.accountId, workspaceDetails?.UUID, cloudContext, setAppState]);

	useAsyncEffect(async () => {
		if (orgId) {
			try {
				const identifiers = {
					atlassianAccountId: currentUser.accountId,
					tenantId: cloudContext ?? undefined,
					atlassianOrgId: orgId,
				};

				const customAttributes = {
					targetApp: 'atlas_web',
					townsquareApp: 'home',
					...(currentUser.locale ? { locale: currentUser.locale } : {}),
				};

				const statsigGateInitPromise = initialiseFeatureGatesFromBootstrapOnce().then(
					(statsigBootstrapped) => {
						if (statsigBootstrapped) {
							return;
						}

						// initialise will be called on the embed path - don't remove.
						return FeatureGates.initializeCompleted()
							? FeatureGates.updateUser(featureGatesOptions, identifiers, customAttributes)
							: FeatureGates.initialize(featureGatesOptions, identifiers, customAttributes);
					},
				);

				const statsigExperimentInitPromise = !initialiseConfigFromBootstrapOnce()
					? initConfigClient(identifiers)
					: Promise.resolve();

				await Promise.all([statsigGateInitPromise, statsigExperimentInitPromise]);
			} catch (e) {
				captureException(e);
			}
		}

		// If we have a matched route and it has a preload function, call it.
		// This will warm up the route for faster navigation.
		if (matchedRoute?.route) {
			preloadRouteComponent(matchedRoute.route);
		}

		setupPlatformGates({
			onFeatureFlagAccessed: (name, value) => {
				if (observabilityFilter(name)) {
					featureFlagAccessedUFOv1(name, value);
					featureFlagAccessedUFOv2(name, value);
					featureFlagAccessedSentry(name, value);
				}
			},
		});
		completeInit();
	}, [currentUser.accountId, completeInit]);

	if (appState === AppState.LOADING) {
		return <FullScreenSpaLoader />;
	}

	return (
		<CollabContext.Provider value={collabContext}>
			<NoopRbacProvider>{props.children({ collabContext, currentUser })}</NoopRbacProvider>
		</CollabContext.Provider>
	);
}
